/**
 * QAIHeader Component (v1.0)
 * Shared header for all Q.AI pages with navigation based on account type
 *
 * Features:
 * - Logo with Q.AI branding
 * - Token balance display with Add button
 * - Navigation based on account type (web, mt5, both)
 * - Logout functionality
 * - Help and Settings access
 *
 * Account Types:
 * - 'web': Web-only user (coach page access)
 * - 'mt5': MT5-only user (MT5 panel access)
 * - 'both': Both web and MT5 access
 *
 * @param {string} accountType - 'web' | 'mt5' | 'both'
 * @param {number} balance - Token balance
 * @param {boolean} isLowBalance - Low balance indicator
 * @param {function} onBuyTokens - Buy tokens callback
 * @param {function} onLogout - Logout callback
 * @param {function} onHelp - Help modal callback
 * @param {string} currentPage - Current page identifier for nav highlighting
 * @param {object} qbridgeStatus - QBridge connection status
 * @param {function} onQBridgeClick - QBridge status click handler
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Button,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AddIcon from '@mui/icons-material/Add';
import ChatIcon from '@mui/icons-material/Chat';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MenuIcon from '@mui/icons-material/Menu';
import QAILogo from './QAILogo';
import TokenBalance from './TokenBalance';
import { getBalance } from '../../api/qaiApi';
import { checkQBridgeStatus, detectMT5, QBRIDGE_STATUS } from '../../services/qbridge.service';

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════
const BRAND = {
  primary: '#27B56D',
  background: '#0A0A0F',
  surface: '#12121A',
  text: '#FFFFFF',
  textMuted: '#71717A',
  textSecondary: '#A1A1AA',
  border: 'rgba(255, 255, 255, 0.06)',
  success: '#27B56D',
  warning: '#F59E0B',
  error: '#EF4444'
};

// ═══════════════════════════════════════════════════════════════════════════════
// NAVIGATION ITEMS BY ACCOUNT TYPE
// ═══════════════════════════════════════════════════════════════════════════════
const NAV_ITEMS = {
  web: [
    { id: 'coach', label: 'Coach', path: '/q-ai/coach', icon: ChatIcon }
  ],
  mt5: [
    { id: 'mt5-setup', label: 'MT5 Setup', path: '/q-ai/mt5-ready', icon: DesktopWindowsIcon }
  ],
  both: [
    { id: 'coach', label: 'Coach', path: '/q-ai/coach', icon: ChatIcon },
    { id: 'mt5-setup', label: 'MT5 Setup', path: '/q-ai/mt5-ready', icon: DesktopWindowsIcon }
  ],
  // Common items for all account types
  common: [
    { id: 'tokens', label: 'Buy Tokens', path: '/q-ai/tokens', icon: AccountBalanceWalletIcon }
  ]
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const QAIHeader = ({
  accountType = 'both',
  balance: externalBalance,
  isLowBalance: externalIsLowBalance,
  onBuyTokens,
  onLogout,
  onHelp,
  currentPage,
  qbridgeStatus: externalQBridgeStatus,
  onQBridgeClick,
  showNavigation = true,
  showQBridgeStatus = true,
  compact = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Internal state for balance (if not provided externally)
  const [internalBalance, setInternalBalance] = useState(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  // Internal state for QBridge status (if not provided externally)
  const [internalQBridgeStatus, setInternalQBridgeStatus] = useState({
    connected: false,
    qbridgeRunning: false,
    mt5Detected: false,
    mt5Account: null,
    mt5Broker: null
  });

  // Mobile menu state
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const isMobileMenuOpen = Boolean(mobileMenuAnchor);

  // Use external values if provided, otherwise use internal state
  const balance = externalBalance !== undefined ? externalBalance : internalBalance;
  const isLowBalance = externalIsLowBalance !== undefined ? externalIsLowBalance : (balance !== null && balance <= 15);
  const qbridgeStatus = externalQBridgeStatus || internalQBridgeStatus;

  // Get token from localStorage
  const token = localStorage.getItem('qai_token');

  // Fetch balance if not provided externally
  useEffect(() => {
    if (externalBalance === undefined && token) {
      const fetchBalance = async () => {
        setIsLoadingBalance(true);
        try {
          const result = await getBalance(token);
          setInternalBalance(result?.balance || 0);
        } catch (err) {
          console.error('[QAIHeader] Failed to fetch balance:', err);
        } finally {
          setIsLoadingBalance(false);
        }
      };
      fetchBalance();
    }
  }, [token, externalBalance]);

  // Check QBridge status if not provided externally
  useEffect(() => {
    if (externalQBridgeStatus || !showQBridgeStatus) return;

    const checkStatus = async () => {
      try {
        const qbridgeResult = await checkQBridgeStatus();
        const qbridgeRunning = qbridgeResult.status === QBRIDGE_STATUS.READY;

        if (qbridgeRunning) {
          const mt5Result = await detectMT5();
          setInternalQBridgeStatus({
            connected: mt5Result.detected,
            qbridgeRunning: true,
            mt5Detected: mt5Result.detected,
            mt5Account: mt5Result.account,
            mt5Broker: mt5Result.broker
          });
        } else {
          setInternalQBridgeStatus({
            connected: false,
            qbridgeRunning: false,
            mt5Detected: false,
            mt5Account: null,
            mt5Broker: null
          });
        }
      } catch (err) {
        setInternalQBridgeStatus({
          connected: false,
          qbridgeRunning: false,
          mt5Detected: false,
          mt5Account: null,
          mt5Broker: null
        });
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 10000);
    return () => clearInterval(interval);
  }, [externalQBridgeStatus, showQBridgeStatus]);

  // Handle logout
  const handleLogout = useCallback(() => {
    if (onLogout) {
      onLogout();
    } else {
      localStorage.removeItem('qai_token');
      localStorage.removeItem('qai_user');
      navigate('/q-ai');
    }
  }, [onLogout, navigate]);

  // Handle buy tokens
  const handleBuyTokens = useCallback(() => {
    if (onBuyTokens) {
      onBuyTokens();
    } else {
      navigate('/q-ai/tokens');
    }
  }, [onBuyTokens, navigate]);

  // Handle help
  const handleHelp = useCallback(() => {
    if (onHelp) {
      onHelp();
    }
  }, [onHelp]);

  // Handle QBridge status click
  const handleQBridgeClick = useCallback(() => {
    if (onQBridgeClick) {
      onQBridgeClick();
    } else if (!qbridgeStatus.connected) {
      navigate('/qbridge/setup');
    }
  }, [onQBridgeClick, qbridgeStatus.connected, navigate]);

  // Handle navigation
  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuAnchor(null);
  };

  // Get navigation items based on account type
  const navItems = [
    ...(NAV_ITEMS[accountType] || NAV_ITEMS.both),
    ...NAV_ITEMS.common
  ];

  // Determine current page from location
  const activePage = currentPage || navItems.find(item => location.pathname === item.path)?.id;

  return (
    <Box
      component="header"
      sx={{
        backgroundColor: BRAND.background,
        borderBottom: `1px solid ${BRAND.border}`,
        px: { xs: 1.5, sm: 2, md: 3 },
        py: compact ? { xs: 0.5, sm: 0.75 } : { xs: 0.75, sm: 1 },
        flexShrink: 0
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
          gap: { xs: 1, sm: 1.5 }
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════════════
            LEFT SECTION: Logo + QBridge Status
            ═══════════════════════════════════════════════════════════════════════ */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.5 }, minWidth: 0 }}>
          {/* Logo */}
          <Box
            onClick={() => navigate('/q-ai')}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <QAILogo size="small" showText textVariant="short" />
          </Box>

          {/* QBridge Status Chip */}
          {showQBridgeStatus && (
            <Tooltip
              title={
                qbridgeStatus.connected
                  ? `Connected: ${qbridgeStatus.mt5Account || 'MT5'} @ ${qbridgeStatus.mt5Broker || 'Broker'}`
                  : qbridgeStatus.qbridgeRunning
                    ? 'QBridge running - MT5 not detected'
                    : 'QBridge not running. Click to setup.'
              }
              placement="bottom"
            >
              <Chip
                icon={qbridgeStatus.connected ? <CheckCircleIcon /> : <ErrorIcon />}
                label="MT5"
                size="small"
                onClick={handleQBridgeClick}
                sx={{
                  backgroundColor: 'transparent',
                  color: qbridgeStatus.connected ? BRAND.success : BRAND.error,
                  border: `1px solid ${qbridgeStatus.connected
                    ? 'rgba(39, 181, 109, 0.25)'
                    : 'rgba(239, 68, 68, 0.25)'}`,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                  height: { xs: '26px', sm: '30px' },
                  cursor: 'pointer',
                  '& .MuiChip-label': { px: { xs: 0.5, sm: 1 } },
                  '& .MuiChip-icon': {
                    color: qbridgeStatus.connected ? BRAND.success : BRAND.error,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    ml: { xs: 0.25, sm: 0.5 }
                  },
                  '&:hover': {
                    backgroundColor: qbridgeStatus.connected
                      ? 'rgba(39, 181, 109, 0.1)'
                      : 'rgba(239, 68, 68, 0.1)'
                  }
                }}
              />
            </Tooltip>
          )}
        </Box>

        {/* ═══════════════════════════════════════════════════════════════════════
            CENTER SECTION: Navigation (Desktop only)
            ═══════════════════════════════════════════════════════════════════════ */}
        {showNavigation && !isMobile && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flex: 1,
              justifyContent: 'center'
            }}
          >
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activePage === item.id;
              return (
                <Button
                  key={item.id}
                  startIcon={<Icon sx={{ fontSize: '1rem' }} />}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    color: isActive ? BRAND.text : BRAND.textSecondary,
                    backgroundColor: isActive ? 'rgba(39, 181, 109, 0.1)' : 'transparent',
                    borderBottom: isActive ? `2px solid ${BRAND.primary}` : '2px solid transparent',
                    borderRadius: '8px 8px 0 0',
                    textTransform: 'none',
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 0.75,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: BRAND.text
                    }
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            RIGHT SECTION: Balance + Actions
            ═══════════════════════════════════════════════════════════════════════ */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5 }, flexShrink: 0 }}>
          {/* Token Balance + Add Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5 } }}>
            <TokenBalance
              balance={balance}
              isLow={isLowBalance}
              onBuyTokens={handleBuyTokens}
              size="small"
            />
            {/* +Add Token Button */}
            <Tooltip title="Add Tokens" arrow>
              <IconButton
                onClick={handleBuyTokens}
                size="small"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  borderRadius: '6px',
                  padding: { xs: '4px 6px', sm: '4px 10px' },
                  minWidth: { xs: 36, sm: 'auto' },
                  minHeight: { xs: 36, sm: 'auto' },
                  backgroundColor: 'rgba(39, 181, 109, 0.12)',
                  border: '1px solid rgba(39, 181, 109, 0.3)',
                  color: BRAND.primary,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(39, 181, 109, 0.2)',
                    borderColor: 'rgba(39, 181, 109, 0.5)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(39, 181, 109, 0.25)'
                  }
                }}
              >
                <AddIcon sx={{ fontSize: { xs: '1.125rem', sm: '1rem' } }} />
                <Typography
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    fontFamily: "'Inter', -apple-system, sans-serif"
                  }}
                >
                  Add
                </Typography>
              </IconButton>
            </Tooltip>
          </Box>

          {/* Help Button */}
          {onHelp && (
            <Tooltip title="Help" arrow>
              <IconButton
                onClick={handleHelp}
                size="small"
                sx={{
                  color: BRAND.textMuted,
                  width: { xs: 44, md: 32 },
                  height: { xs: 44, md: 32 },
                  '&:hover': {
                    color: '#7c3aed',
                    backgroundColor: 'rgba(124, 58, 237, 0.08)'
                  }
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: '1.125rem' }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Settings Button (Desktop only) */}
          {!isMobile && (
            <Tooltip title="Settings" arrow>
              <IconButton
                size="small"
                sx={{
                  color: BRAND.textMuted,
                  width: 32,
                  height: 32,
                  '&:hover': {
                    color: BRAND.primary,
                    backgroundColor: 'rgba(39, 181, 109, 0.08)'
                  }
                }}
              >
                <SettingsIcon sx={{ fontSize: '1.125rem' }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Logout Button (Desktop) / Menu Button (Mobile) */}
          {isMobile && showNavigation ? (
            <>
              <IconButton
                onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
                sx={{
                  color: BRAND.textMuted,
                  width: 44,
                  height: 44,
                  '&:hover': {
                    color: BRAND.text,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={isMobileMenuOpen}
                onClose={() => setMobileMenuAnchor(null)}
                PaperProps={{
                  sx: {
                    backgroundColor: BRAND.surface,
                    border: `1px solid ${BRAND.border}`,
                    minWidth: 200
                  }
                }}
              >
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activePage === item.id;
                  return (
                    <MenuItem
                      key={item.id}
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        color: isActive ? BRAND.primary : BRAND.textSecondary,
                        backgroundColor: isActive ? 'rgba(39, 181, 109, 0.1)' : 'transparent',
                        gap: 1.5,
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
                      }}
                    >
                      <Icon sx={{ fontSize: '1.25rem' }} />
                      {item.label}
                    </MenuItem>
                  );
                })}
                <Divider sx={{ borderColor: BRAND.border, my: 1 }} />
                <MenuItem
                  onClick={() => { setMobileMenuAnchor(null); }}
                  sx={{ color: BRAND.textSecondary, gap: 1.5 }}
                >
                  <SettingsIcon sx={{ fontSize: '1.25rem' }} />
                  Settings
                </MenuItem>
                <MenuItem
                  onClick={handleLogout}
                  sx={{ color: BRAND.error, gap: 1.5 }}
                >
                  <LogoutIcon sx={{ fontSize: '1.25rem' }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Tooltip title="Logout" arrow>
              <IconButton
                onClick={handleLogout}
                size="small"
                sx={{
                  color: BRAND.textMuted,
                  width: { xs: 44, md: 32 },
                  height: { xs: 44, md: 32 },
                  '&:hover': {
                    color: BRAND.error,
                    backgroundColor: 'rgba(239, 68, 68, 0.08)'
                  }
                }}
              >
                <LogoutIcon sx={{ fontSize: '1.125rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default QAIHeader;
